import * as React from "react";
import { RouteComponentProps } from "react-router";
import { RioConfirmModal, RioIf, RioLoadingIf, RioResources, RioValidator, RioDateHelpers, RioIconButton, RioForEach, IRioModelErrors, RioCheckbox } from "rio.react.all";
import { Page } from "../components/Page";
import { ErrorModal } from "../modals/ErrorModal";
import { HttpErrorHelper } from "../helpers/HttpErrorHelper";
import { AccountStatementApi } from "../apiLayer/AccountStatementApi";
import { AccountStatementEmailSendRequestApiModel } from "../apiModels/AccountStatementEmailSendRequestApiModel";
import { NavisionDocumentApiModel } from "../apiModels/NavisionDocumentApiModel";
import { DataVineGridView } from "../components/GridView/GridView";
import { IGridViewColumn } from "../components/GridView/IGridViewColumn";
import { IGridViewAction } from "../components/GridView/IGridViewAction";


interface IAccountStatementsSendProps extends Partial<RouteComponentProps> {
}

interface IAccountStatementsSendState {
    loading: boolean;
    processing: boolean;

    statementOptions: Array<NavisionDocumentApiModel>;

    sendProcessedSuccessfully: boolean;
    selectedCount: number;

    reportOnlyClicked: boolean;
    testRunClicked: boolean;
    sendClicked: boolean;
    attachReservesList: boolean;
    showAttachReservesListCheckbox: boolean;
    bulkDeleteClicked: boolean;

    deleteStatementID: number;
    errorMessages: Array<string>;
}
export class AccountStatementsSend extends React.Component<IAccountStatementsSendProps, IAccountStatementsSendState> {
    constructor(props: Readonly<IAccountStatementsSendProps>) {
        super(props);

        this.state = {
            loading: true,
            processing: false,

            statementOptions: [],

            sendProcessedSuccessfully: false,
            selectedCount: 0,

            reportOnlyClicked: false,
            testRunClicked: false,
            sendClicked: false,
            attachReservesList: false,
            showAttachReservesListCheckbox: false,
            bulkDeleteClicked: false,

            deleteStatementID: null,
            errorMessages: []
        };
    }

    private validator = new RioValidator();
    private gridviewRef = React.createRef<DataVineGridView<NavisionDocumentApiModel>>();

    columns: Array<IGridViewColumn<NavisionDocumentApiModel>> = [
        { title: RioResources.find("CustomerNo").text, value: (item) => item.customerAccountNo },
        { title: RioResources.find("CustomerName").text, value: (item) => item.customerName },
        { title: RioResources.find("Email").text, value: (item) => item.customerEmail },
        { title: RioResources.find("DocumentNo").text, value: (item) => item.documentNumber },
        { title: RioResources.find("CreateDate").text, value: (item) => item.fileCreateDate, displayValue: (item) => RioDateHelpers.toDateFormat(item.fileCreateDate, "DD/MM/YYYY HH:mm:ss") }
    ];

    actions: Array<IGridViewAction<NavisionDocumentApiModel>> = [
        { resource: RioResources.find("Delete"), onClick: (item) => this.setState({ deleteStatementID: item.navisionDocumentID }), default: false, hasPermission: true }
    ];

    render() {
        return (
            <Page heading={RioResources.find("AccountStatements")}>
                <RioLoadingIf condition={!!this.state.loading || this.state.processing}>
                    <RioIf condition={this.state.errorMessages != null && this.state.errorMessages.length > 0} then={
                        <div className="validationSummary">
                            <RioForEach items={this.state.errorMessages} perform={(error, index) => {
                                return <div key={`${index}`} className="fieldValidationError">{error}</div>;
                            }} />
                        </div>
                        
                    } />
                    <RioIf condition={this.state.sendProcessedSuccessfully} then={
                        <div className="sendAccountStatementsProcessingMsgWrapper">
                            <span>{RioResources.find("SendAccountStatementsProcessingMsg").text}</span>
                        </div>
                    } />

                    <div className="accountStatementButtonsWrapper">
                        <div className="left">
                            <RioIconButton resource={this.state.selectedCount !== this.state.statementOptions.length ? RioResources.find("SelectAll") : RioResources.find("DeSelectAll")} hideImage={true} onClick={() => this.selectAndDeselectAll()} disabled={this.state.processing} hasPermission={true} />
                            <RioIconButton resource={RioResources.find("SelectVisible")} hideImage={true} onClick={() => this.selectVisible()} disabled={this.state.processing} hasPermission={true} />
                            <div className="deleteSelectedWrapper">
                                <RioIconButton resource={RioResources.find("DeleteSelected")} onClick={() => this.setState({ bulkDeleteClicked: this.state.selectedCount > 0 })} disabled={this.state.processing} hasPermission={true} />
                            </div>
                        </div>
                        <div className="right">
                            <RioIf condition={this.state.showAttachReservesListCheckbox} then={
                                <div className="attachReservesListSection">
                                    <label>{RioResources.find("AttachReservesList").text}</label>
                                    <RioCheckbox checked={this.state.attachReservesList} onChange={() => this.setState({ attachReservesList: !this.state.attachReservesList })} />
                                </div>
                            } />
                            <RioIconButton resource={RioResources.find("ReportOnly")} onClick={() => this.setState({ reportOnlyClicked: true })} disabled={this.state.processing} hasPermission={true} />
                            <RioIconButton resource={RioResources.find("TestRun")} onClick={() => this.setState({ testRunClicked: true })} disabled={this.state.processing} hasPermission={true} />
                            <RioIconButton resource={RioResources.find("Send")} onClick={() => this.setState({ sendClicked: true })} disabled={this.state.processing} hasPermission={true} />
                        </div>
                    </div>


                    <DataVineGridView<NavisionDocumentApiModel>
                        ref={this.gridviewRef}
                        noResults={RioResources.find("NoResultsFound")}
                        columns={this.columns}
                        actions={this.actions}
                        items={this.state.statementOptions}
                        pageSize={50}
                        rowKey={(item: NavisionDocumentApiModel) => item.navisionDocumentID}
                        rowIsSelectable={() => true}
                        selectableRowTitle={RioResources.find("Selected").text}
                        onSelectedChanged={(items) => {
                            this.setState({
                                selectedCount: items != null && !!items.length ? items.length : 0 //nullable operators don't work with whatever is building the typescript
                            });
                        }}
                    />

                    <RioIf condition={!!this.state.reportOnlyClicked} then={
                        <RioConfirmModal text={RioResources.find("ReportOnlyConfirmMsg")} onConfirmModalClosed={(result) => this.reportOnlyConfirmed(result)} className="confirmModal" />
                    } />

                    <RioIf condition={!!this.state.testRunClicked} then={
                        <RioConfirmModal text={RioResources.find("TestRunConfirmMsg")} onConfirmModalClosed={(result) => this.testRunConfirmed(result)} className="confirmModal" />
                    } />

                    <RioIf condition={!!this.state.sendClicked} then={
                        <RioConfirmModal text={RioResources.find("SendAccountStatementConfirmMsg")} onConfirmModalClosed={(result) => this.sendConfirmed(result)} className="confirmModal" />
                    } />

                    <RioIf condition={!!this.state.deleteStatementID} then={
                        <RioConfirmModal text={RioResources.find("DeleteDocumentConfirmMsg")} onConfirmModalClosed={(result) => this.confirmStatementDeleted(result)} className="confirmModal" />
                    } />

                    <RioIf condition={!!this.state.bulkDeleteClicked} then={
                        <RioConfirmModal text={RioResources.find("BulkDeleteStatementsConfirmMsg")} onConfirmModalClosed={(result) => this.confirmBulkDelete(result)} className="confirmModal" />
                    } />

                </RioLoadingIf>
            </Page>
        );
    }

    componentDidMount(): void {
        this.getSettings();
        this.getStatements();
    }

    getStatements(): void {
        AccountStatementApi.getPendingStatements()
            .then((response) => {
                this.setState({
                    statementOptions: response,
                    selectedCount: response.length,
                    loading: false
                });
                this.gridviewRef.current.selectAll();
            });
    }

    getSettings(): void {
        AccountStatementApi.getShowAttachReservesListCheckbox()
            .then((response) => {
                this.setState({
                    showAttachReservesListCheckbox: response
                });
            })
    }

    selectAndDeselectAll() {
        if (this.state.selectedCount !== this.state.statementOptions.length) {
            this.gridviewRef.current.selectAll();
        } else {
            this.gridviewRef.current.deselectAll();
        }
    }

    selectVisible() {
        this.gridviewRef.current.selectVisibleItems();
    }

    deleteSelected() {

    }

    confirmStatementDeleted(result: boolean): void {
        this.clearErrorMessages();

        if (result) {
            AccountStatementApi.deleteStatement(this.state.deleteStatementID)
                .then(() => {
                    let options: Array<NavisionDocumentApiModel> = JSON.parse(JSON.stringify(this.state.statementOptions));
                    let index = options.findIndex(x => x.navisionDocumentID == this.state.deleteStatementID);
                    if (index > -1) {
                        options.splice(index, 1);
                    }
                    this.setState({
                        deleteStatementID: null,
                        statementOptions: options
                    });
                })
                .catch((response) => {
                    //this.validator.addModelErrors(this, response.errors);
                    this.appendErrorMessages(response.errors)
                    this.setState({
                        deleteStatementID: null
                    });
                });
        } else {
            this.setState({
                deleteStatementID: null
            });
        }
    }

    confirmBulkDelete(result: boolean): void {
        this.clearErrorMessages();

        if (result) {
            let selectedStatements = this.gridviewRef.current.getSelectedItems();
            AccountStatementApi.bulkDeleteStatements(selectedStatements)
                .then(() => {
                    window.location.reload(); //successful completion results in a refresh to ensure correct data present.
                })
                .catch((response) => {
                    this.appendErrorMessages(response.errors);
                    this.setState({
                        bulkDeleteClicked: false
                    });
                });
        } else {
            this.setState({
                bulkDeleteClicked: false
            });
        }
    }

    appendErrorMessages(errors: IRioModelErrors) {
        let errorMessages: Array<string> = []; //ValidationSummary is currently broken, so use this as a quick workaround
        for (var key in errors["ValidationSummary"]) {
            errorMessages.push(errors["ValidationSummary"][key]);
        }

        this.setState({
            errorMessages: errorMessages
        });
    }

    clearErrorMessages() {
        this.setState({
            errorMessages: []
        });
    }

    reportOnlyConfirmed(result: boolean): void {
        if (result) {
            this.send(true, false);
        } 

        this.setState({
            reportOnlyClicked: false
        });
    }

    testRunConfirmed(result: boolean): void {
        if (result) {
            this.send(false, true);
        }

        this.setState({
            testRunClicked: false
        });
    }

    sendConfirmed(result: boolean): void {
        if (result) {
            this.send(false, false);
        }

        this.setState({
            sendClicked: false
        });
    }

    send(reportOnly: boolean, testRun: boolean): void {
        this.clearErrorMessages();

        let request = new AccountStatementEmailSendRequestApiModel();
        request.reportOnly = reportOnly;
        request.testMode = testRun;
        request.attachReservesList = this.state.attachReservesList;
        request.statements = this.gridviewRef.current.getSelectedItems();

        AccountStatementApi.queueSend(request)
            .then((response) => {
                this.setState({
                    sendProcessedSuccessfully: true,
                    processing: false
                });
                this.getStatements();
            })
            .catch((response) => {
                //this.validator.addModelErrors(this, response.errors);
                this.appendErrorMessages(response.errors);
                this.setState({
                    processing: false
                });
                if (!reportOnly && !testRun) {
                    this.getStatements();
                }
            });

        this.setState({
            processing: true
        });
    }
}